import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls/Button/Button";
import {
  MinusIcon,
  PlusIcon,
  ReloadIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Box,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";

import { getPunchCountDisplay } from "../../../utils/getPunchCountDisplay";
import {
  PunchPassListItem_PunchPass$key,
  PunchPassTemplateType,
} from "./__generated__/PunchPassListItem_PunchPass.graphql";

export type PunchPassListItemProps = {
  punchPassRef: PunchPassListItem_PunchPass$key;
  isAvailable: boolean;
  isApplied: boolean;
  hasSameTypeApplied?: boolean;
  onApply: (id: string, type?: PunchPassTemplateType) => void;
  onRemove: (id: string) => void;
};

export const PunchPassListItem = ({
  punchPassRef,
  isAvailable,
  isApplied,
  hasSameTypeApplied = false,
  onApply,
  onRemove,
}: PunchPassListItemProps) => {
  const punchPass = useFragment(PunchPassListItemFragment, punchPassRef);

  const handleRemovePunchPass = () => {
    onRemove(punchPass.id);
  };

  return (
    <Box
      key={punchPass.id}
      height={64}
      paddingHorizontal={4}
      borderWidth={1.5}
      borderColor={isApplied ? "outline-main-primary" : "surface-dividers"}
      borderRadius="2"
      backgroundColor={isAvailable ? "surface-primary" : "surface-secondary"}
      justifyContent="center"
    >
      <HStack justifyContent="space-between" alignItems="center">
        <VStack gap={1}>
          <Text variant="mainSmallSemiBold">
            {punchPass.punchPassTemplate?.name}
          </Text>
          <HStack>
            <Text variant="mainSmall" color="text-secondary">
              {punchPass.client?.fullName}
            </Text>
            <Text variant="mainSmall" color="text-secondary">
              &nbsp;&bull;&nbsp;
            </Text>
            <Text variant="mainSmall" color="text-secondary">
              {getPunchCountDisplay(
                punchPass.remainingPunchCount,
                punchPass.punchPassTemplate?.calculationType ?? "PER_BOOKING",
                true
              )}
            </Text>
          </HStack>
        </VStack>
        {isApplied ? (
          <Button
            accessibilityLabel={t`Remove punch pass`}
            testID="remove-punch-pass-button"
            variant="secondary"
            size="tiny"
            leftIcon={<MinusIcon />}
            onPress={handleRemovePunchPass}
          />
        ) : isAvailable ? (
          <Button
            accessibilityLabel={
              hasSameTypeApplied ? t`Replace punch pass` : t`Apply punch pass`
            }
            testID="apply-punch-pass-button"
            variant="main"
            size="tiny"
            leftIcon={hasSameTypeApplied ? <ReloadIcon /> : <PlusIcon />}
            onPress={() => {
              onApply(punchPass.id, punchPass.punchPassTemplate?.type);
            }}
          />
        ) : null}
      </HStack>
    </Box>
  );
};

const PunchPassListItemFragment = graphql`
  fragment PunchPassListItem_PunchPass on PunchPass {
    id
    remainingPunchCount
    client {
      ... on Member {
        fullName
      }
      ... on Customer {
        fullName
      }
    }
    punchPassTemplate {
      name
      type
      calculationType
    }
  }
`;
